import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { OrderAttributes, OrderDetailAttributes } from '@albatrosdeveloper/ave-models-package';

import { DocumentTypeEnum } from 'app/common/enums/document-types.enum';
import { BatchesDetailComponent } from './batches-detail.component';

/** Qué arreglo del detalle se muestra: lo entregado al cliente o lo cargado. */
export type BatchesKind = 'delivered' | 'loaded';

/**
 * Abre el modal de lotes y etiquetas con la configuración que corresponde al tipo de documento.
 *
 * La regla vivía duplicada en cada listado, y por eso tres pantallas del monitor quedaron con un
 * botón que llamaba a un método inexistente y dos listados de ventas nunca lo tuvieron. Acá está
 * una sola vez.
 *
 * El detalle guarda dos arreglos con significados distintos: `batches` son los lotes cargados y
 * `deliveredBatches` los que se entregaron al cliente. Una entrega de salida debe leer lo
 * entregado; el resto de los documentos, lo cargado.
 */
@Injectable({ providedIn: 'root' })
export class BatchesDetailDialogService {
  private readonly dialog = inject(MatDialog);

  /** Documentos que se editan sobre lo entregado. */
  private static readonly DELIVERED_EDITABLE = [
    DocumentTypeEnum.CAMBIO_DE_PRODUCTO,
    DocumentTypeEnum.DEVOLUCION_DE_PRODUCTO,
  ];

  /** Documentos que se editan sobre lo cargado. */
  private static readonly LOADED_EDITABLE = [
    DocumentTypeEnum.SOLICITUD_CAMBIO_PRODUCTO,
    DocumentTypeEnum.SOLICITUD_DEVOLUCION_PRODUCTO,
  ];

  /** `type` e `isEdit` con los que el modal decide qué arreglo mostrar y si permite editarlo. */
  configFor(orderTypeCode: DocumentTypeEnum | string): { type: string; isEdit: boolean } {
    const code = orderTypeCode as DocumentTypeEnum;

    if (code === DocumentTypeEnum.ENTREGA_SALIDA) {
      return { type: 'deliveryBatches', isEdit: false };
    }
    if (BatchesDetailDialogService.DELIVERED_EDITABLE.includes(code)) {
      return { type: 'deliveryBatches', isEdit: true };
    }
    if (BatchesDetailDialogService.LOADED_EDITABLE.includes(code)) {
      return { type: 'normal', isEdit: true };
    }
    return { type: 'normal', isEdit: false };
  }

  /**
   * Si la línea tiene algo que mostrar. Mira el arreglo que el modal abriría y, cuando ese viene
   * vacío, el otro: los documentos anteriores a la selección manual de etiquetas solo tienen
   * `batches`, y dejar el botón apagado ahí escondería información que sí existe.
   */
  hasBatches(
    orderTypeCode: DocumentTypeEnum | string,
    detail: OrderDetailAttributes,
    kind?: BatchesKind,
  ): boolean {
    const type = kind ? BatchesDetailDialogService.typeOf(kind) : this.configFor(orderTypeCode).type;
    const delivered = detail?.deliveredBatches?.length ?? 0;
    const loaded = detail?.batches?.length ?? 0;

    return type === 'normal' ? loaded > 0 : delivered > 0 || loaded > 0;
  }

  /**
   * `kind` fuerza qué arreglo abrir, para las pantallas donde la pestaña manda sobre el tipo del
   * documento: en el listado de entregas, la pestaña "Entrega" muestra lo entregado y la pestaña
   * "Factura" lo facturado, aunque las dos cuelguen del mismo documento seleccionado.
   */
  open(
    order: OrderAttributes,
    detail: OrderDetailAttributes,
    options: { kind?: BatchesKind; isChangeCostBatch?: boolean; updatePermission?: string } = {},
  ) {
    const resolved = options.kind
      ? { type: BatchesDetailDialogService.typeOf(options.kind), isEdit: false }
      : this.configFor(order?.type?.code);

    return this.dialog.open(BatchesDetailComponent, {
      width: '90%',
      disableClose: true,
      data: {
        order,
        data: detail,
        type: resolved.type,
        isEdit: resolved.isEdit,
        isChangeCostBatch: options.isChangeCostBatch ?? false,
        updatePermission: options.updatePermission,
      },
    });
  }

  private static typeOf(kind: BatchesKind): string {
    return kind === 'delivered' ? 'deliveryBatches' : 'normal';
  }
}
